import {DotFillIcon, MoonIcon, ShieldIcon, SyncIcon} from '@primer/octicons-react'
import type {CompletedReview, PendingReviewRequest} from '../../../page-data/payloads/review-requests'
import styles from './Reviewers.module.css'
import {pullRequestsLayoutRoute} from '../../../routes/layout-route'
import {pullRequestsChangesWithRangeRoute} from '../../../routes/files-changed-routes'
import {useRouteParams} from '@github-ui/react-core/future/use-route-params'
import {IconButton} from '@primer/react'
import {clsx} from 'clsx'
import {Suspense} from 'react'
import {UserSectionSkeleton} from '../UserSectionSkeleton'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {Link} from '@github-ui/react-core/link'
import {ReviewerIdentity} from './ReviewerIdentity'
import {reviewState, reviewStateTooltip, deferredTooltip, codeOwnerTooltip} from './review-state-helpers'
import {useReRequestReviewMutation} from '../../../mutations/use-re-request-review-mutation'
import {useReviewers} from '../data/use-reviewers'
import {TeamReviewSummary} from './TeamReviewSummary'
import {isCopilot} from '@github-ui/assignees/copilot-user'

export function Reviewers() {
  return (
    <ErrorBoundary
      boundaryName="Reviewers"
      fallback={<span className={styles.emptyState}>Unable to load reviewers</span>}
    >
      <Suspense fallback={<UserSectionSkeleton placeholderRows={2} />}>
        <div className="d-flex flex-column gap-3">
          <ReviewersComponent />
          <TeamReviewSummary />
        </div>
      </Suspense>
    </ErrorBoundary>
  )
}

export function ReviewersComponent() {
  const {data: reviewers} = useReviewers()

  const completedReviews = reviewers.completedReviews ?? []
  const pendingRequests = reviewers.pendingReviewRequests ?? []

  if (completedReviews.length === 0 && pendingRequests.length === 0) {
    return <span className={styles.emptyState}>No reviewers</span>
  }

  const writeAccessReviews = completedReviews.filter(r => r.hasWriteAccess || isCopilot(r.reviewer.displayLogin))
  const nonWriteAccessReviews = completedReviews.filter(r => !r.hasWriteAccess && !isCopilot(r.reviewer.displayLogin))

  return (
    <div className="mt-2">
      <ul className={styles.reviewerList}>
        {writeAccessReviews.map(review => (
          <CompletedReviewRow key={`completed-${review.id}`} review={review} muted={!review.hasWriteAccess} />
        ))}
        {pendingRequests.map(request => (
          <PendingReviewRow key={`pending-${request.reviewer.id}`} request={request} />
        ))}
      </ul>
      {nonWriteAccessReviews.length > 0 && (
        <ul className={styles.reviewerList}>
          {nonWriteAccessReviews.map(review => (
            <CompletedReviewRow key={`completed-${review.id}`} review={review} muted />
          ))}
        </ul>
      )}
    </div>
  )
}

function CompletedReviewRow({review, muted = false}: {review: CompletedReview; muted?: boolean}) {
  const {owner, repo, pr_number} = useRouteParams(pullRequestsLayoutRoute)
  const name = review.reviewer.displayLogin
  const {icon: stateIcon, className: stateClass} = reviewState(review.state)
  const {mutate: reRequestReview} = useReRequestReviewMutation()
  const changesWithRangePath = pullRequestsChangesWithRangeRoute.generatePath({
    owner,
    repo,
    pr_number,
    range: `BASE..${review.headSha}`,
  })

  return (
    <li className={styles.reviewerRow}>
      <ReviewerIdentity reviewer={review.reviewer} />
      <span className={styles.indicators}>
        {review.canReRequest && (
          <IconButton
            icon={SyncIcon}
            aria-label={`Re-request review from ${name}`}
            variant="invisible"
            size="small"
            tooltipDirection="nw"
            className={clsx(styles.statusIcon, 'color-fg-muted')}
            onClick={() => reRequestReview({reviewerLogin: name})}
          />
        )}
        <IconButton
          as={Link}
          to={changesWithRangePath}
          icon={stateIcon}
          aria-label={reviewStateTooltip(review.state, name)}
          variant="invisible"
          size="small"
          tooltipDirection="nw"
          className={clsx(styles.statusIcon, muted ? 'color-fg-muted' : stateClass)}
        />
      </span>
    </li>
  )
}

function PendingReviewRow({request}: {request: PendingReviewRequest}) {
  const name = request.reviewer.displayLogin
  return (
    <li className={styles.reviewerRow}>
      <ReviewerIdentity reviewer={request.reviewer} />
      <span className={styles.indicators}>
        {request.deferredType !== null && (
          <IconButton
            icon={MoonIcon}
            aria-label={deferredTooltip(request.deferredType, name)}
            variant="invisible"
            size="small"
            tooltipDirection="nw"
            className={clsx(styles.statusIcon, 'color-fg-muted')}
          />
        )}
        {(request.isCodeOwner || request.isCodeOwnerFromDelegation) && (
          <IconButton
            icon={ShieldIcon}
            aria-label={codeOwnerTooltip(request)}
            variant="invisible"
            size="small"
            tooltipDirection="nw"
            className={clsx(styles.statusIcon, 'color-fg-muted')}
          />
        )}
        <IconButton
          icon={DotFillIcon}
          aria-label={`Awaiting requested review from ${name}`}
          variant="invisible"
          size="small"
          tooltipDirection="nw"
          className={clsx(styles.statusIcon, 'color-fg-attention')}
        />
      </span>
    </li>
  )
}
